import { useState, useEffect, useCallback } from "react";
import { IT_WORDS, EN_WORDS } from "./wordLists";

// ─── WORD LISTS ───────────────────────────────────────────────────────────────

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const MAX_GUESSES = 6;
const WORD_LEN = 5;

const KEYBOARD_IT = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "⌫"]
];
const KEYBOARD_EN = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "⌫"]
];

const TEXTS = {
  it: {
    title: "SYNTHWORD",
    subtitle: "IL GIOCO DELLE PAROLE",
    start: "GIOCA",
    howto: "Indovina la parola in 6 tentativi",
    green: "🟩 Lettera corretta, posizione corretta",
    yellow: "🟨 Lettera corretta, posizione errata",
    gray: "⬜ Lettera non presente",
    win: ["MOSTRUOSO!", "OTTIMO!", "MOLTO BUONO!", "BUONO!", "EVVAI!", "PER UN SOFFIO!"],
    lose: (w) => `La parola era: ${w}`,
    invalid: "Parola non valida!",
    play_again: "RIGIOCA",
    attempts: "Tentativo",
    of: "di",
    lang_label: "LINGUA",
  },
  en: {
    title: "SYNTHWORD",
    subtitle: "THE WORD GAME",
    start: "PLAY",
    howto: "Guess the word in 6 tries",
    green: "🟩 Correct letter, correct spot",
    yellow: "🟨 Correct letter, wrong spot",
    gray: "⬜ Letter not in word",
    win: ["MONSTROUS!", "EXCELLENT!", "VERY GOOD!", "GOOD!", "YEAH!", "BY A WHISKER!"],
    lose: (w) => `The word was: ${w}`,
    invalid: "Not a valid word!",
    play_again: "PLAY AGAIN",
    attempts: "Attempt",
    of: "of",
    lang_label: "LANGUAGE",
  }
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function getRandom(collection) {
  // Converts arrays or sets to an array and returns a random element, or an empty string when no elements exist.
  const arr = Array.isArray(collection) ? collection : Array.from(collection);
  if (!arr.length) return "";
  return arr[Math.floor(Math.random() * arr.length)];
}

function normalizeWordArray(words) {
  // Converts a possible word array into a sanitized uppercase list with only 5-letter strings and no duplicates.
  if (!Array.isArray(words)) return [];
  const unique = new Set();
  words.forEach((word) => {
    if (typeof word !== "string") return;
    const normalized = word.trim().toUpperCase();
    if (normalized.length === WORD_LEN) unique.add(normalized);
  });
  return Array.from(unique);
}

function mergeWordArrays(...arrays) {
  // Merges multiple word arrays into a single deduplicated array while preserving insertion order.
  const unique = new Set();
  arrays.forEach((arr) => {
    arr.forEach((word) => unique.add(word));
  });
  return Array.from(unique);
}

function normalizeLanguageWordPools(rawWords) {
  // Normalizes legacy and split-list word list shapes into solution and valid-guess sets with safe fallbacks.
  if (Array.isArray(rawWords)) {
    const legacyWords = normalizeWordArray(rawWords);
    return {
      solutionSet: new Set(legacyWords),
      validSet: new Set(legacyWords),
    };
  }

  if (!rawWords || typeof rawWords !== "object") {
    return {
      solutionSet: new Set(),
      validSet: new Set(),
    };
  }

  const solutionWords = normalizeWordArray(
    rawWords.list1 ?? rawWords.solutionWords ?? rawWords.solution ?? rawWords.words
  );
  const list2Words = normalizeWordArray(rawWords.list2);
  const explicitValidWords = normalizeWordArray(rawWords.validWords ?? rawWords.acceptedWords);

  const mergedValidWords = explicitValidWords.length
    ? mergeWordArrays(solutionWords, list2Words, explicitValidWords)
    : mergeWordArrays(solutionWords, list2Words);

  const fallbackSolutionWords = solutionWords.length ? solutionWords : mergedValidWords;

  return {
    solutionSet: new Set(fallbackSolutionWords),
    validSet: new Set(mergedValidWords),
  };
}

function evaluateGuess(guess, target) {
  const result = Array(WORD_LEN).fill("absent");
  const targetArr = target.split("");
  const guessArr = guess.split("");
  const used = Array(WORD_LEN).fill(false);

  // First pass: correct
  for (let i = 0; i < WORD_LEN; i++) {
    if (guessArr[i] === targetArr[i]) {
      result[i] = "correct";
      used[i] = true;
    }
  }
  // Second pass: present
  for (let i = 0; i < WORD_LEN; i++) {
    if (result[i] === "correct") continue;
    for (let j = 0; j < WORD_LEN; j++) {
      if (!used[j] && guessArr[i] === targetArr[j]) {
        result[i] = "present";
        used[j] = true;
        break;
      }
    }
  }
  return result;
}

const WORD_POOLS_IT = normalizeLanguageWordPools(IT_WORDS);
const WORD_POOLS_EN = normalizeLanguageWordPools(EN_WORDS);

// ─── STYLES ───────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:        #0a0015;
    --bg2:       #12002a;
    --pink:      #ff2d78;
    --cyan:      #00f5ff;
    --purple:    #bf00ff;
    --yellow:    #ffe600;
    --gold:      #ffaa00;
    --green:     #00ff9f;
    --dark-grid: #1a0035;
    --tile-empty:#1c0040;
    --tile-fill: #2d0060;
    --text:      #f0e6ff;
    --dim:       #7a5a99;
  }

  body { background: var(--bg); font-family: 'Rajdhani', sans-serif; color: var(--text); }

  .sw-root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-x: hidden;
    position: relative;
    background: var(--bg);
  }

  /* ── GRID BACKGROUND ── */
  .sw-root::before {
    content: '';
    position: fixed; inset: 0;
    background:
      linear-gradient(rgba(0,245,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,245,255,0.04) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
    z-index: 0;
  }

  /* ── HORIZON GLOW ── */
  .sw-root::after {
    content: '';
    position: fixed;
    bottom: 0; left: 0; right: 0;
    height: 320px;
    background: linear-gradient(to top, rgba(191,0,255,0.18) 0%, transparent 100%);
    pointer-events: none;
    z-index: 0;
  }

  .sw-content {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 520px;
    padding: 0 12px 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  /* ── HEADER ── */
  .sw-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px 0 6px;
    position: relative;
    border-bottom: 1px solid rgba(0,245,255,0.15);
    margin-bottom: 8px;
  }

  .sw-title {
    font-family: 'Orbitron', sans-serif;
    font-size: clamp(22px, 6vw, 32px);
    font-weight: 900;
    letter-spacing: 6px;
    background: linear-gradient(90deg, var(--pink), var(--cyan), var(--purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: none;
    filter: drop-shadow(0 0 18px var(--pink));
  }

  .sw-subtitle {
    font-family: 'Rajdhani', sans-serif;
    font-size: 11px;
    letter-spacing: 4px;
    color: var(--cyan);
    opacity: 0.7;
    margin-top: 2px;
    text-align: center;
  }

  /* ── LANG TOGGLE ── */
  .lang-toggle-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 14px 0 4px;
  }

  .lang-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 9px;
    letter-spacing: 3px;
    color: var(--dim);
  }

  .lang-toggle {
    position: relative;
    width: 90px;
    height: 30px;
    background: var(--tile-empty);
    border: 1px solid rgba(0,245,255,0.25);
    border-radius: 4px;
    cursor: pointer;
    overflow: hidden;
    display: flex;
  }

  .lang-toggle-option {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    color: var(--dim);
    transition: color 0.2s;
    z-index: 1;
    cursor: pointer;
  }

  .lang-toggle-option.active {
    color: var(--bg);
  }

  .lang-toggle-slider {
    position: absolute;
    top: 2px; bottom: 2px;
    width: calc(50% - 2px);
    background: linear-gradient(135deg, var(--cyan), var(--purple));
    border-radius: 2px;
    transition: left 0.25s cubic-bezier(0.4,0,0.2,1);
    box-shadow: 0 0 10px var(--cyan);
  }

  /* ── START SCREEN ── */
  .sw-start {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    margin-top: 40px;
    padding: 32px 28px;
    background: rgba(18,0,42,0.85);
    border: 1px solid rgba(191,0,255,0.3);
    border-radius: 8px;
    width: 100%;
    box-shadow: 0 0 40px rgba(191,0,255,0.12), inset 0 0 30px rgba(0,0,0,0.4);
  }

  .sw-start-logo {
    font-family: 'Orbitron', sans-serif;
    font-size: clamp(28px, 8vw, 48px);
    font-weight: 900;
    background: linear-gradient(135deg, var(--pink) 0%, var(--cyan) 50%, var(--purple) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 24px var(--pink));
    text-align: center;
    letter-spacing: 8px;
  }

  .sw-howto {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .sw-howto-item {
    font-size: 13px;
    color: rgba(240,230,255,0.75);
    letter-spacing: 1px;
  }

  .sw-start-btn {
    font-family: 'Orbitron', sans-serif;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 5px;
    padding: 14px 40px;
    background: transparent;
    border: 2px solid var(--cyan);
    color: var(--cyan);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    overflow: hidden;
  }

  .sw-start-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0,245,255,0.1), rgba(191,0,255,0.1));
    opacity: 0;
    transition: opacity 0.2s;
  }

  .sw-start-btn:hover {
    box-shadow: 0 0 24px var(--cyan), 0 0 50px rgba(0,245,255,0.15);
    transform: translateY(-2px);
  }
  .sw-start-btn:hover::before { opacity: 1; }

  /* ── GAME STATUS ── */
  .sw-status {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 3px;
    color: var(--cyan);
    opacity: 0.6;
    margin: 4px 0 8px;
  }

  /* ── TOAST ── */
  .sw-toast {
    position: fixed;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(26,0,53,0.97);
    border: 1px solid var(--pink);
    color: var(--pink);
    font-family: 'Orbitron', sans-serif;
    font-size: 12px;
    letter-spacing: 3px;
    padding: 10px 24px;
    border-radius: 4px;
    z-index: 100;
    box-shadow: 0 0 20px var(--pink);
    animation: toastIn 0.2s ease;
    text-align: center;
    max-width: 90vw;
  }

  @keyframes toastIn {
    from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
  }

  /* ── GRID ── */
  .sw-grid {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 10px 0;
  }

  .sw-row {
    display: flex;
    gap: 6px;
  }

  /* ── TILE ── */
  .sw-tile {
    width: 58px;
    height: 58px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Orbitron', sans-serif;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 1px;
    border: 2px solid var(--dark-grid);
    background: var(--tile-empty);
    color: var(--text);
    border-radius: 4px;
    transition: border-color 0.1s, transform 0.1s;
    user-select: none;
    position: relative;
  }

  .sw-tile.filled {
    border-color: rgba(191,0,255,0.5);
    background: var(--tile-fill);
    animation: tilePop 0.1s ease;
  }

  .sw-tile.shake {
    animation: tileShake 0.4s ease;
  }

  @keyframes tilePop {
    0%   { transform: scale(0.85); }
    60%  { transform: scale(1.08); }
    100% { transform: scale(1); }
  }

  @keyframes tileShake {
    0%,100% { transform: translateX(0); }
    20%     { transform: translateX(-6px); }
    40%     { transform: translateX(6px); }
    60%     { transform: translateX(-4px); }
    80%     { transform: translateX(4px); }
  }

  .sw-tile.flip-0 { animation: tileFlip 0.5s 0.0s ease both; }
  .sw-tile.flip-1 { animation: tileFlip 0.5s 0.1s ease both; }
  .sw-tile.flip-2 { animation: tileFlip 0.5s 0.2s ease both; }
  .sw-tile.flip-3 { animation: tileFlip 0.5s 0.3s ease both; }
  .sw-tile.flip-4 { animation: tileFlip 0.5s 0.4s ease both; }

  @keyframes tileFlip {
    0%   { transform: rotateX(0); }
    49%  { transform: rotateX(-90deg); }
    50%  { transform: rotateX(-90deg); }
    100% { transform: rotateX(0); }
  }

  .sw-tile.correct {
    background: linear-gradient(135deg, #005533, #00ff9f22);
    border-color: var(--green);
    color: var(--green);
    box-shadow: 0 0 14px rgba(0,255,159,0.4);
  }

  .sw-tile.present {
    background: linear-gradient(135deg, #553300, #ffe60022);
    border-color: var(--gold);
    color: var(--gold);
    box-shadow: 0 0 14px rgba(255,170,0,0.35);
  }

  .sw-tile.absent {
    background: #160030;
    border-color: #3a1a5a;
    color: #5a3a7a;
  }

  /* ── WIN ROW PULSE ── */
  .sw-tile.win-pulse {
    animation: winPulse 0.6s ease both;
  }
  .sw-tile.win-pulse-0 { animation-delay: 0.0s; }
  .sw-tile.win-pulse-1 { animation-delay: 0.1s; }
  .sw-tile.win-pulse-2 { animation-delay: 0.2s; }
  .sw-tile.win-pulse-3 { animation-delay: 0.3s; }
  .sw-tile.win-pulse-4 { animation-delay: 0.4s; }

  @keyframes winPulse {
    0%   { transform: scale(1); box-shadow: 0 0 0 rgba(0,255,159,0); }
    50%  { transform: scale(1.18); box-shadow: 0 0 30px rgba(0,255,159,0.7); }
    100% { transform: scale(1); }
  }

  /* ── KEYBOARD ── */
  .sw-keyboard {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-top: 14px;
    width: 100%;
  }

  .sw-kb-row {
    display: flex;
    justify-content: center;
    gap: 5px;
  }

  .sw-key {
    min-width: 34px;
    height: 52px;
    padding: 0 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Orbitron', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    background: var(--tile-fill);
    border: 1px solid rgba(191,0,255,0.3);
    color: var(--text);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .sw-key:hover {
    background: rgba(191,0,255,0.25);
    border-color: var(--purple);
    box-shadow: 0 0 10px rgba(191,0,255,0.3);
  }
  .sw-key:active { transform: scale(0.93); }

  .sw-key.wide {
    min-width: 58px;
    font-size: 9px;
    letter-spacing: 1px;
  }

  .sw-key.correct {
    background: linear-gradient(135deg, #004428, #00ff9f22);
    border-color: var(--green);
    color: var(--green);
    box-shadow: 0 0 8px rgba(0,255,159,0.3);
  }
  .sw-key.present {
    background: linear-gradient(135deg, #442200, #ffe60022);
    border-color: var(--gold);
    color: var(--gold);
    box-shadow: 0 0 8px rgba(255,170,0,0.25);
  }
  .sw-key.absent {
    background: #0e001f;
    border-color: #280050;
    color: #3d2060;
  }

  /* ── RESULT PANEL ── */
  .sw-result {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .sw-result-msg {
    font-family: 'Orbitron', sans-serif;
    font-size: clamp(15px,4vw,20px);
    font-weight: 900;
    letter-spacing: 5px;
    background: linear-gradient(90deg, var(--pink), var(--cyan));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 12px var(--pink));
    text-align: center;
  }

  .sw-result-word {
    font-family: 'Orbitron', sans-serif;
    font-size: 13px;
    letter-spacing: 4px;
    color: var(--gold);
    opacity: 0.9;
    text-align: center;
  }

  .sw-replay-btn {
    font-family: 'Orbitron', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 4px;
    padding: 12px 30px;
    background: transparent;
    border: 2px solid var(--pink);
    color: var(--pink);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .sw-replay-btn:hover {
    box-shadow: 0 0 20px var(--pink), 0 0 40px rgba(255,45,120,0.15);
    transform: translateY(-2px);
  }

  /* ── SCANLINES ── */
  .sw-scanlines {
    pointer-events: none;
    position: fixed;
    inset: 0;
    z-index: 200;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0,0,0,0.06) 2px,
      rgba(0,0,0,0.06) 4px
    );
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 400px) {
    .sw-tile { width: 50px; height: 50px; font-size: 18px; }
    .sw-key { min-width: 28px; height: 44px; font-size: 9px; }
    .sw-key.wide { min-width: 48px; }
  }
`;

// ─── COMPONENTS ───────────────────────────────────────────────────────────────

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = styles;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

function LangToggle({ lang, onChange }) {
  const isEN = lang === "en";
  return (
    <div className="lang-toggle-wrap">
      <span className="lang-label">LANG</span>
      <div className="lang-toggle" onClick={() => onChange(isEN ? "it" : "en")}>
        <div
          className="lang-toggle-slider"
          style={{ left: isEN ? "calc(50% + 2px)" : "2px" }}
        />
        <div className={`lang-toggle-option${!isEN ? " active" : ""}`}>IT</div>
        <div className={`lang-toggle-option${isEN ? " active" : ""}`}>EN</div>
      </div>
    </div>
  );
}

function StartScreen({ lang, onLangChange, onStart }) {
  const t = TEXTS[lang];
  return (
    <div className="sw-start">
      <div className="sw-start-logo">{t.title}</div>
      <LangToggle lang={lang} onChange={onLangChange} />
      <div className="sw-howto">
        <div className="sw-howto-item">{t.howto}</div>
        <div className="sw-howto-item">{t.green}</div>
        <div className="sw-howto-item">{t.yellow}</div>
        <div className="sw-howto-item">{t.gray}</div>
      </div>
      <button className="sw-start-btn" onClick={onStart}>{t.start}</button>
    </div>
  );
}

function Tile({ letter, state, flipIdx, shake, winPulse }) {
  let cls = "sw-tile";
  if (letter) cls += " filled";
  if (state) {
    cls += ` ${state}`;
    if (flipIdx !== undefined) cls += ` flip-${flipIdx}`;
  }
  if (shake) cls += " shake";
  if (winPulse !== undefined) cls += ` win-pulse win-pulse-${winPulse}`;
  return <div className={cls}>{letter}</div>;
}

function Grid({ guesses, evaluations, currentGuess, shakeRow, winRow }) {
  const rows = [];
  for (let r = 0; r < MAX_GUESSES; r++) {
    const cells = [];
    const isSubmitted = r < guesses.length;
    const isCurrent = r === guesses.length;
    for (let c = 0; c < WORD_LEN; c++) {
      let letter = "";
      let state = null;
      let flipIdx;
      let shake = false;
      let winPulse;
      if (isSubmitted) {
        letter = guesses[r][c] || "";
        state = evaluations[r]?.[c] || null;
        flipIdx = c;
        if (winRow === r) winPulse = c;
      } else if (isCurrent) {
        letter = currentGuess[c] || "";
        if (letter) state = null;
        shake = shakeRow === r;
      }
      cells.push(
        <Tile key={c} letter={letter} state={state} flipIdx={flipIdx} shake={shake} winPulse={winPulse} />
      );
    }
    rows.push(<div className="sw-row" key={r}>{cells}</div>);
  }
  return <div className="sw-grid">{rows}</div>;
}

function Keyboard({ layout, keyStates, onKey }) {
  return (
    <div className="sw-keyboard">
      {layout.map((row, ri) => (
        <div className="sw-kb-row" key={ri}>
          {row.map((k) => {
            const isWide = k === "ENTER" || k === "⌫";
            const ks = keyStates[k] || "";
            return (
              <button
                key={k}
                className={`sw-key${isWide ? " wide" : ""}${ks ? " " + ks : ""}`}
                onClick={() => onKey(k)}
              >
                {k}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── MAIN GAME ────────────────────────────────────────────────────────────────
// Renders the Synthword game and coordinates language-specific word pools, rounds, and UI state.
export default function SynthwaveWordle() {
  const [screen, setScreen] = useState("start"); // "start" | "game"
  const [lang, setLang] = useState("it");
  const [target, setTarget] = useState("");
  const [guesses, setGuesses] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [current, setCurrent] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [toast, setToast] = useState("");
  const [shakeRow, setShakeRow] = useState(null);
  const [winRow, setWinRow] = useState(null);
  const { validSet } = lang === "it" ? WORD_POOLS_IT : WORD_POOLS_EN;
  const keyboard = lang === "it" ? KEYBOARD_IT : KEYBOARD_EN;
  const t = TEXTS[lang];

  // Starts a new run resetting all round-dependent game state.
  function startGame(l = lang) {
    const pools = l === "it" ? WORD_POOLS_IT : WORD_POOLS_EN;
    const solutionPool = pools.solutionSet.size ? pools.solutionSet : pools.validSet;
    setTarget(getRandom(solutionPool));
    setGuesses([]);
    setEvaluations([]);
    setCurrent("");
    setGameOver(false);
    setWon(false);
    setToast("");
    setShakeRow(null);
    setWinRow(null);
    setScreen("game");
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 1800);
  }

  // Build key states from evaluations
  const keyStates = {};
  guesses.forEach((g, ri) => {
    g.split("").forEach((ch, ci) => {
      const s = evaluations[ri]?.[ci];
      if (!s) return;
      const prev = keyStates[ch];
      if (prev === "correct") return;
      if (prev === "present" && s !== "correct") return;
      keyStates[ch] = s;
    });
  });

  // Handles keyboard input, validates guesses against the accepted set, and advances the game state.
  const handleKey = useCallback((k) => {
    if (gameOver || screen !== "game") return;

    if (k === "⌫" || k === "BACKSPACE") {
      setCurrent(c => c.slice(0, -1));
      return;
    }
    if (k === "ENTER") {
      if (current.length < WORD_LEN) {
        showToast(lang === "it" ? "Troppo corta!" : "Too short!");
        setShakeRow(guesses.length);
        setTimeout(() => setShakeRow(null), 500);
        return;
      }
      if (!validSet.has(current)) {
        showToast(t.invalid);
        setShakeRow(guesses.length);
        setTimeout(() => setShakeRow(null), 500);
        return;
      }
      const eval_ = evaluateGuess(current, target);
      const newGuesses = [...guesses, current];
      const newEvals = [...evaluations, eval_];
      setGuesses(newGuesses);
      setEvaluations(newEvals);
      setCurrent("");

      if (current === target) {
        setWon(true);
        setGameOver(true);
        const ri = newGuesses.length - 1;
        setTimeout(() => {
          setWinRow(ri);
          showToast(t.win[Math.min(ri, t.win.length - 1)]);
        }, 300);
      } else if (newGuesses.length >= MAX_GUESSES) {
        setGameOver(true);
        setTimeout(() => showToast(t.lose(target)), 300);
      }
      return;
    }

    // Letter
    if (/^[A-Z]$/i.test(k) && current.length < WORD_LEN) {
      setCurrent(c => (c + k.toUpperCase()).slice(0, WORD_LEN));
    }
  }, [current, guesses, evaluations, target, gameOver, screen, validSet, t, lang]);

  // Physical keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const k = e.key.toUpperCase();
      if (k === "BACKSPACE") handleKey("⌫");
      else if (k === "ENTER") handleKey("ENTER");
      else if (/^[A-Z]$/.test(k)) handleKey(k);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleKey]);

  function handleLangChange(l) {
    setLang(l);
    if (screen === "game") startGame(l);
  }

  const attemptInfo = screen === "game" && !gameOver
    ? `${t.attempts} ${guesses.length + 1} ${t.of} ${MAX_GUESSES}`
    : "";

  return (
    <>
      <StyleInjector />
      <div className="sw-scanlines" />
      <div className="sw-root">
        <div className="sw-content">
          {/* HEADER */}
          <div className="sw-header">
            <div style={{ textAlign: "center" }}>
              <div className="sw-title">{t.title}</div>
              <div className="sw-subtitle">{t.subtitle}</div>
            </div>
          </div>

          {/* START SCREEN */}
          {screen === "start" && (
            <StartScreen
              lang={lang}
              onLangChange={setLang}
              onStart={() => startGame(lang)}
            />
          )}

          {/* GAME SCREEN */}
          {screen === "game" && (
            <>
              {/* Lang toggle during game */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
                <LangToggle lang={lang} onChange={handleLangChange} />
                {attemptInfo && (
                  <div className="sw-status">{attemptInfo}</div>
                )}
              </div>

              <Grid
                guesses={guesses}
                evaluations={evaluations}
                currentGuess={current}
                shakeRow={shakeRow}
                winRow={winRow}
              />

              <Keyboard layout={keyboard} keyStates={keyStates} onKey={handleKey} />

              {gameOver && (
                <div className="sw-result">
                  {won
                    ? <div className="sw-result-msg">{t.win[Math.min(guesses.length - 1, t.win.length - 1)]}</div>
                    : <div className="sw-result-word">{t.lose(target)}</div>
                  }
                  <button className="sw-replay-btn" onClick={() => startGame(lang)}>
                    {t.play_again}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {toast && <div className="sw-toast">{toast}</div>}
      </div>
    </>
  );
}
